import { setInteractionModality } from '@react-aria/interactions';
import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { currentOS, OS } from '../../lib/platform';
import { RouteManager, useHomeyPath } from '../../RouteManager';

import { useEventCallback } from '../../hooks/useEventCallback';

import { CommandPaletteDialog } from './CommandPaletteDialog';
import { useCommandPalette } from './CommandPaletteStore';

const selectPaletteState = (state) => {
  return {
    open: state.open,
    close: state.close,
  };
};

export function CommandPaletteController() {
  const homeyPath = useHomeyPath();

  const isEnabled = homeyPath != null && homeyPath !== '';

  const { close } = useCommandPalette(selectPaletteState, shallow);

  useEffect(() => {
    if (isEnabled !== true) {
      close();
    }
  }, [close, isEnabled]);

  if (isEnabled !== true) {
    return null;
  }

  return <CommandPaletteControllerEnabled />;
}

function CommandPaletteControllerEnabled() {
  const location = useLocation();
  // Navigating drops the history state, so the palette also closes when a command navigates.
  const isOpen =
    RouteManager.getDialogValue(location.state, RouteManager.dialogState.commandPaletteDialog) ===
    true;

  const { open } = useCommandPalette(selectPaletteState, shallow);

  const onKeyDown = useEventCallback((event) => {
    if (event.repeat === true) {
      return;
    }

    if (event.target instanceof Element) {
      const editableElement = event.target.closest(
        'input, textarea, [contenteditable], [contenteditable="true"], [contenteditable="plaintext-only"]',
      );

      if (editableElement != null) {
        return;
      }
    }

    if (event.key.toLowerCase() !== 'k') {
      return;
    }

    const isModifierPressed = currentOS === OS.MACOS ? event.metaKey : event.ctrlKey;

    if (isModifierPressed !== true || event.altKey === true || event.shiftKey === true) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (isOpen === true) {
      return;
    }

    // react-aria ignores key presses with a modifier, so the shortcut wouldn't count as keyboard
    // use. The dialog shows the highlighted result right away when opened with the keyboard.
    setInteractionModality('keyboard');
    open();
  });

  useEffect(() => {
    document.documentElement.addEventListener('keydown', onKeyDown);

    return function cleanup() {
      document.documentElement.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  // AnimatePresence keeps the dialog mounted until its exit animation has finished.
  return <AnimatePresence>{isOpen === true && <CommandPaletteDialog />}</AnimatePresence>;
}
